import { Fragment } from "react";

import { isEmpty } from "lodash/fp";

import { Divider, Typography } from "@mui/material";

import ErrorState from "@app/analytics/components/ErrorState";
import { InterruptionsGradientRange } from "@app/analytics/components/GradientRange/CoachingGradientRange";
import TableCell from "@app/analytics/components/Table/TableCell";
import { getRoundedValues } from "@app/analytics/utils";
import { getInterruptionsDescription } from "@app/analytics/utils/coachingUtils";
import { HeadCell } from "@app/common/hooks/useDataTable";
import { CoachingSession } from "@app/common/types";
import { pluralizePhrase } from "@app/common/utils";
import { getInterruptionsSemanticScore } from "@app/common/utils/coachingUtils";

import { Column, Row } from "@rui/Stack";

import CoachingMoments, {
  CoachingMomentProps,
} from "./components/CoachingMoments";
import CoachingTable from "./components/CoachingTable";
import useCoachingDashboardData from "./hooks/useCoachingDashboardData";
import {
  Card,
  CardText,
  EmptyCardText,
  LoadingSkeleton,
  SectionDescription,
  SectionHeading,
  SectionOverviewContainer,
  SectionSubHeading,
  ValueWithSemanticIcon,
} from "./styles";

const INTERRUPTIONS_HEAD_CELLS: HeadCell<CoachingSession>[] = [
  {
    id: "count",
    label: "# of Interruptions",
  },
  {
    id: "words",
    label: "Most common",
    sortBy: (d) => d.words[0]?.displayValue.toLowerCase(),
  },
];

const getMomentLabel: CoachingMomentProps["getMomentLabel"] = (moment) =>
  `You interrupt ${moment.words[0].displayValue}`;

const Interruptions = () => {
  const { interruptions, isError, isInitialLoading, refetch } =
    useCoachingDashboardData();

  const mostFrequentlyInterrupted = interruptions.words.slice(0, 3);
  const interruptionRate = interruptions.rate;
  const descriptionSegments = getInterruptionsDescription(interruptionRate);

  if (isError) {
    return <ErrorState refetch={refetch} />;
  }

  return (
    <>
      <Column spacing={1}>
        <Row variant="apart">
          <SectionHeading>Interruptions</SectionHeading>
          {interruptionRate != null && (
            <ValueWithSemanticIcon
              score={interruptions.semanticScore}
              variant="h3"
              sx={{ fontWeight: 500 }}
            >
              {getRoundedValues(interruptionRate).formattedValue} per 10 turns
            </ValueWithSemanticIcon>
          )}
        </Row>
        <SectionDescription>
          {descriptionSegments.map((segment) =>
            segment.style ? (
              <span key={segment.value} style={segment.style}>
                {segment.value}
              </span>
            ) : (
              <Fragment key={segment.value}>{segment.value}</Fragment>
            )
          )}
        </SectionDescription>
        <SectionOverviewContainer spacing={1}>
          <Card title="Range" subtitle="Your interruptions / 10 turns">
            {isInitialLoading ? (
              <LoadingSkeleton width="100%" />
            ) : interruptionRate == null ? (
              <EmptyCardText />
            ) : (
              <InterruptionsGradientRange count={interruptionRate} />
            )}
          </Card>
          <Card title="Who you interrupt the most">
            {isInitialLoading ? (
              <LoadingSkeleton width="100%" />
            ) : isEmpty(mostFrequentlyInterrupted) ? (
              <EmptyCardText />
            ) : (
              <CardText>
                {mostFrequentlyInterrupted
                  .map(
                    ({ displayValue, count }) =>
                      `${displayValue} (${pluralizePhrase(count, "time")})`
                  )
                  .join(", ")}{" "}
              </CardText>
            )}
          </Card>
        </SectionOverviewContainer>
      </Column>
      <Divider />
      <Column spacing={1}>
        <SectionSubHeading>Moments</SectionSubHeading>
        <SectionDescription>
          Review moments where you interrupted others.
        </SectionDescription>
        <CoachingMoments
          getMomentLabel={getMomentLabel}
          isLoading={isInitialLoading}
          moments={interruptions.moments}
          type="interruptions"
        />
      </Column>
      <Divider />
      <Column spacing={1}>
        <SectionSubHeading>Meetings</SectionSubHeading>
        <SectionDescription>
          Review meeting reports where you interrupted others.
        </SectionDescription>
        <CoachingTable
          defaultOrderBy="count"
          isLoading={isInitialLoading}
          renderSectionCells={(session) => {
            const mostCommonlyInterrupted = session.words[0];
            return (
              <>
                <TableCell>
                  <ValueWithSemanticIcon
                    score={getInterruptionsSemanticScore(session.count)}
                    variant="bodySecondaryReg"
                    sx={{ minWidth: "30px", textAlign: "right" }}
                  >
                    {session.count}
                  </ValueWithSemanticIcon>
                </TableCell>
                <TableCell>
                  <Typography
                    sx={{
                      font: "inherit",
                      maxWidth: "200px",
                      whiteSpace: "break-spaces",
                    }}
                  >
                    {mostCommonlyInterrupted
                      ? `${mostCommonlyInterrupted.displayValue}${
                          mostCommonlyInterrupted.count > 1
                            ? ` (${mostCommonlyInterrupted.count})`
                            : ""
                        }`
                      : "-"}
                  </Typography>
                </TableCell>
              </>
            );
          }}
          rows={interruptions.sessions}
          sectionHeadCells={INTERRUPTIONS_HEAD_CELLS}
        />
      </Column>
    </>
  );
};

export default Interruptions;
